/**
 * /search route — the full-page twin of the Cmd-K overlay. The input is locally controlled (so typing
 * is snappy and never blocks on router state), and the term is MIRRORED into the URL (?q=) on a short
 * debounce so a search is shareable + survives reload and the back button works. On first mount the
 * input is seeded from ?q= (a shared/bookmarked link). Same drill routing as the overlay — selecting a
 * result navigates to the right Customer-360 tab.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { Input } from "@/components/ui";
import { drillPath, type DrillTarget } from "@/features/search/drill";
import { useSearch } from "@/features/search/useSearch";
import { SearchResults } from "@/features/search/SearchResults";

export function SearchPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  // Local state is the source of truth for the input; ?q= seeds it once and then mirrors it.
  const [term, setTerm] = useState(() => params.get("q") ?? "");
  const search = useSearch(term);

  // Mirror the term into the URL (replace, so typing doesn't spam history). Debounced lightly so each
  // keystroke doesn't churn the history entry.
  const lastSynced = useRef(term);
  useEffect(() => {
    if (term === lastSynced.current) return;
    const id = setTimeout(() => {
      lastSynced.current = term;
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (term) p.set("q", term);
          else p.delete("q");
          return p;
        },
        { replace: true },
      );
    }, 250);
    return () => clearTimeout(id);
  }, [term, setParams]);

  function handleSelect(target: DrillTarget) {
    navigate(drillPath(target));
  }

  return (
    <section aria-labelledby="search-title" className="flex flex-col gap-5">
      <h1 id="search-title" className="text-(length:--text-2xl) font-semibold text-(--text-strong)">
        {t("nav.search")}
      </h1>

      <div className="relative max-w-2xl">
        <Search
          className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-(--text-subtle)"
          aria-hidden
        />
        <Input
          type="search"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder={t("shell.omniboxPlaceholder")}
          aria-label={t("search.inputLabel")}
          autoComplete="off"
          spellCheck={false}
          autoFocus
          className="h-11 pl-10 text-(length:--text-base)"
        />
      </div>

      <div className="max-w-2xl">
        <SearchResults
          term={search.debounced}
          active={search.active}
          canSearch={search.canSearch}
          data={search.data}
          isLoading={search.isLoading}
          isError={search.isError}
          error={search.error}
          onSelect={handleSelect}
          onRetry={search.refetch}
          density="page"
        />
      </div>
    </section>
  );
}
